/**
 * Centralized TanStack Query keys (T024, W4). The ONLY source of query keys.
 *
 * Hierarchy `['job', id, ...]` lets a single
 * `invalidateQueries({ queryKey: qk.job(id) })` invalidate the whole subtree
 * (detail + logs + frames). This is pure frontend — it never touches rm-api.
 */
export const qk = {
  jobs: () => ['jobs'] as const,
  topazVersions: () => ['jobs', 'topaz-versions'] as const,
  catalogClients: () => ['catalog', 'clients'] as const,
  catalogProjects: (clientId: string) =>
    ['catalog', 'clients', clientId, 'projects'] as const,
  catalogUnits: (projectId: string, view: 'all' | 'unique' = 'all') =>
    ['catalog', 'projects', projectId, 'units', view] as const,
  renderVersions: (unitId: string) =>
    ['catalog', 'units', unitId, 'render-versions'] as const,
  renderVersion: (versionId: string) => ['render-versions', versionId] as const,
  deliveryAttempts: (versionId: string) =>
    ['render-versions', versionId, 'deliveries'] as const,
  floorplanVersions: (unitId: string) =>
    ['catalog', 'units', unitId, 'floorplan-versions'] as const,
  floorplanVersion: (versionId: string) =>
    ['floorplan-versions', versionId] as const,
  publicFloorplan: (unitId: string) => ['public-floorplan', unitId] as const,
  job: (id: string) => ['job', id] as const,
  jobAttempts: (id: string, status: string) =>
    ['job', id, 'attempts', status] as const,
  jobLogs: (id: string) => ['job', id, 'logs'] as const,
  frames: (id: string) => ['job', id, 'frames'] as const,
  assetHubStyles: () => ['asset-hub', 'styles'] as const,
  assetHubAssets: (filters: object = {}) =>
    ['asset-hub', 'assets', filters] as const,
  assetHubAsset: (stableId: string) =>
    ['asset-hub', 'asset', stableId] as const,
  assetHubTasks: (stableId: string) =>
    ['asset-hub', 'asset', stableId, 'tasks'] as const,
  assetHubTaskQueue: (status: string) =>
    ['asset-hub', 'tasks', status] as const,
  me: () => ['me'] as const,
  seedUsers: () => ['dev', 'seed-users'] as const,
  apiKeys: () => ['api-keys'] as const,
  organizations: () => ['organizations'] as const,
  organization: (id: string) => ['organizations', id] as const,
  orgMembers: (id: string) => ['organizations', id, 'members'] as const,
  pluginAccess: (id: string) => ['organizations', id, 'plugin-access'] as const,
  hostAnalyticsPolicy: (id: string) =>
    ['organizations', id, 'host-analytics-policy'] as const,
  orgInvitations: (id: string) => ['organizations', id, 'invitations'] as const,
  myInvitations: () => ['organizations', 'my-invitations'] as const,
  upscalingPresets: () => ['upscaling-presets'] as const,
  adminUsers: () => ['admin', 'users'] as const,
  adminExternalApiKeys: () => ['admin', 'external-api-keys'] as const,
  adminOrganizations: () => ['admin', 'organizations'] as const,
  adminOrganizationProducts: (id: string) =>
    ['admin', 'organizations', id, 'products'] as const,
  adminVersions: () => ['admin', 'versions'] as const,
  adminBackgroundJobs: () => ['admin', 'background-jobs'] as const,
  adminOperations: () => ['admin', 'operations'] as const,
  adminOperationsAlert: () => ['admin', 'operations', 'alert'] as const,
  adminCodeAnalysis: () => ['admin', 'code-analysis'] as const,
  productAnalytics: (filters: object = {}) =>
    ['admin', 'product-analytics', filters] as const,
  artifactVersions: () => ['admin', 'artifact-versions'] as const,
  schedulerSettings: () => ['admin', 'scheduler-settings'] as const,
  machines: () => ['admin', 'machines'] as const,
  machineStatus: (id: string) => ['admin', 'machines', id, 'status'] as const,
  machineSessions: (id: string) =>
    ['admin', 'machines', id, 'sessions'] as const,
  machineLogs: (id: string) => ['admin', 'machines', id, 'logs'] as const,
} as const
